// src/data/workflowsClient.js
//
// TASK-4557: Client for /api/workflows.
//
// All methods are async and return camelCase domain objects.
// Pattern mirrors projectsClient.js.

import {
  NetworkError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

const BASE = "/api/workflows";

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return res.json();
}

/** List all workflow templates with phase/run counts. */
export async function getWorkflowTemplates() {
  const list = await apiFetch(`${BASE}/templates`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/templates");
    return [];
  }
  return list;
}

/** Fetch a single template with its nested phases + substeps. */
export async function getWorkflowTemplate(id) {
  return apiFetch(`${BASE}/templates/${id}`);
}

/** Create a new blank template. */
export async function createWorkflowTemplate(payload) {
  return apiFetch(`${BASE}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Save Builder edits — replaces name/description/category and/or the full
 * phase + substep tree. Each phase may include `dependsOnPhaseIndex`
 * (0-based index into the submitted `phases` array) instead of a raw id,
 * since ids are re-minted server-side on every save.
 */
export async function updateWorkflowTemplate(id, payload) {
  return apiFetch(`${BASE}/templates/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Active, non-deactivated users for the run-detail assignee picker. */
export async function getWorkflowAssignableUsers() {
  const list = await apiFetch(`${BASE}/users`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/users");
    return [];
  }
  return list;
}

/** List all workflow runs (id, name, template, status). */
export async function getWorkflowRuns() {
  const list = await apiFetch(`${BASE}/runs`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/runs");
    return [];
  }
  return list;
}

/** Fetch a single run with nested phases + substeps + context. */
export async function getWorkflowRun(id) {
  return apiFetch(`${BASE}/runs/${id}`);
}

/** Guided Launch "Start workflow" — creates a run from a template. */
export async function createWorkflowRun(payload) {
  return apiFetch(`${BASE}/runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Create a remediation run for the Data Trust pillar.
 * POST /api/workflows/runs/remediation
 *
 * @param {{ name: string, source: { type: string, id: string, label: string },
 *            phases: Array<{ name: string, count: number, surfaceLabel: string, surfacePath: string }> }} payload
 */
export async function createDataTrustRemediationRun(payload) {
  return apiFetch(`${BASE}/runs/remediation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Advance a run's current phase to done and unlock the next one. */
export async function advanceWorkflowRunPhase(runId, phaseId) {
  return apiFetch(`${BASE}/runs/${runId}/phases/${phaseId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/** Manually change a run's status (e.g. on_hold / in_progress / completed). */
export async function updateWorkflowRunStatus(runId, status) {
  return apiFetch(`${BASE}/runs/${runId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
}

/** WORKFLOWS-SLICE-3: reassign a phase's owner (admin/editor only). */
export async function assignWorkflowRunPhaseOwner(runId, phaseId, userId) {
  return apiFetch(`${BASE}/runs/${runId}/phases/${phaseId}/owner`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
}

/** WORKFLOWS-SLICE-3: reassign a sub-step's assignee (admin/editor only). */
export async function assignWorkflowRunSubstep(runId, substepId, userId) {
  return apiFetch(`${BASE}/runs/${runId}/substeps/${substepId}/assign`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }),
  });
}

/** WORKFLOWS-SLICE-3: assignee-only sub-step completion toggle. */
export async function completeWorkflowRunSubstep(runId, substepId, done = true) {
  return apiFetch(`${BASE}/runs/${runId}/substeps/${substepId}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ done }),
  });
}

/**
 * WORKFLOWS-SLICE-3: "Need attention" — my (or all, for admin/editor)
 * actionable items. Server returns { scope, substeps, phases } grouped by
 * type; flatten into a single list of { id, label, runId, runName,
 * phaseName, assigneeUserName } items for display.
 */
export async function getWorkflowNeedAttention(scope = "mine") {
  const params = new URLSearchParams({ scope });
  const data = await apiFetch(`${BASE}/need-attention?${params.toString()}`);
  const substeps = (data?.substeps || []).map((s) => ({
    id: `substep-${s.id}`,
    label: s.label,
    runId: s.runId,
    runName: s.runName,
    phaseName: s.phaseName,
    assigneeUserName: s.assigneeUserName,
    dueDate: s.dueDate,
  }));
  const phases = (data?.phases || []).map((p) => ({
    id: `phase-${p.runPhaseId}`,
    label: `Advance phase: ${p.phaseName}`,
    runId: p.runId,
    runName: p.runName,
    phaseName: p.phaseName,
    assigneeUserName: p.ownerUserName,
    dueDate: p.dueDate,
    slaState: p.slaState,
  }));
  return [...substeps, ...phases];
}

/** WORKFLOWS-SLICE-3: in-app notification inbox for the current user. */
export async function getWorkflowNotifications(unreadOnly = false) {
  const params = unreadOnly ? "?unreadOnly=1" : "";
  const list = await apiFetch(`${BASE}/notifications${params}`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/notifications");
    return [];
  }
  return list;
}

/** WORKFLOWS-SLICE-3: mark one notification read. */
export async function markWorkflowNotificationRead(id) {
  return apiFetch(`${BASE}/notifications/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/** WORKFLOWS-SLICE-3: mark all of the current user's notifications read. */
export async function markAllWorkflowNotificationsRead() {
  return apiFetch(`${BASE}/notifications/read-all`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/** WORKFLOWS-SLICE-3: role→person mapping table, for owner/assignee pickers. */
export async function getWorkflowRoleMappings() {
  const list = await apiFetch(`${BASE}/role-mappings`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/role-mappings");
    return [];
  }
  return list;
}

/** WORKFLOWS-SLICE-4: approve or reject a phase's pending approval. */
export async function decideWorkflowRunPhaseApproval(runId, phaseId, decision, note) {
  return apiFetch(`${BASE}/runs/${runId}/phases/${phaseId}/approvals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision, note }),
  });
}

/** WORKFLOWS-SLICE-4: approve or reject a sub-step's pending approval. */
export async function decideWorkflowRunSubstepApproval(runId, substepId, decision, note) {
  return apiFetch(`${BASE}/runs/${runId}/substeps/${substepId}/approvals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ decision, note }),
  });
}

/** WORKFLOWS-SLICE-4: re-open a fresh pending approval after a phase-level rejection. */
export async function resubmitWorkflowRunPhaseApproval(runId, phaseId) {
  return apiFetch(`${BASE}/runs/${runId}/phases/${phaseId}/approvals/resubmit`, {
    method: "POST",
  });
}

/** WORKFLOWS-SLICE-4: re-open a fresh pending approval after a sub-step-level rejection. */
export async function resubmitWorkflowRunSubstepApproval(runId, substepId) {
  return apiFetch(`${BASE}/runs/${runId}/substeps/${substepId}/approvals/resubmit`, {
    method: "POST",
  });
}

/**
 * W1b: Mark a manual substep as notified (pass).
 * Calls the existing /complete endpoint; an optional note is included in the
 * activity log on the server side.
 */
export async function markWorkflowSubstepNotified(runId, substepId, note = null) {
  return apiFetch(`${BASE}/runs/${runId}/substeps/${substepId}/complete`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ done: true, note }),
  });
}

/**
 * W4 — CLOSURE-WF-REACTIVE-LAUNCH: find the first open closure run
 * (status in_progress or on_hold) whose source_location_id matches the given
 * locationId. Used as a client-side dedup guard before opening the drawer.
 * Returns the matching run object or null when none exists.
 */
export async function getOpenClosureRunForLocation(locationId) {
  if (!locationId) return null;
  const runs = await getWorkflowRuns();
  return (
    runs.find(
      (r) =>
        r.sourceLocationId === locationId &&
        r.templateName === "Office Closure" &&
        (r.status === "in_progress" || r.status === "on_hold"),
    ) ?? null
  );
}

/**
 * WORKFLOWS-SLICE-2-LAUNCH-FROM-OBJECT: source→run indicator.
 * Fetch runs launched from a given object (decision/location/lease/market/signal)
 * so the originating view can show "N workflow(s) started from here".
 */
export async function getLinkedWorkflowRuns(sourceType, sourceId) {
  if (!sourceType || !sourceId) return [];
  const params = new URLSearchParams({ sourceType, sourceId: String(sourceId) });
  const list = await apiFetch(`${BASE}/runs/linked?${params.toString()}`);
  if (!Array.isArray(list)) {
    log.warn("workflowsClient: unexpected non-array from GET /api/workflows/runs/linked");
    return [];
  }
  return list;
}
